<template>
  <section :class="$style.section">
    <div id="price-evolution" ref="price-evolution" class="layout-container">
      <div :class="[$style.card, { [$style.is24]: !isHg }]" data-test="market-trends-card">
        <div :class="$style.content">
          <HgBadge theme="primarylight" :class="$style.badge">
            {{ t('myProperties.landing.marketTrends.badge') }}
          </HgBadge>

          <h2 :class="$style.title">{{ t('myProperties.landing.marketTrends.title') }}</h2>

          <p :class="$style.description">
            {{ t('myProperties.landing.marketTrends.description') }}
          </p>

          <HgButton size="large" theme="secondary" :class="$style.cta" @click="goToOverview">
            {{ t('myProperties.landing.marketTrends.cta') }}
          </HgButton>
        </div>

        <div :class="$style.imageWrapper">
          <img
            :src="phoneMockupImage"
            :alt="t('myProperties.landing.marketTrends.phoneMockupAlt')"
            :class="$style.image"
            loading="lazy"
          />
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { isPlatformHg } from '@/helpers/Platform/Platform';
import { getPropertyLandingImageUrl } from '@/helpers/Url/Url';
import { OVERVIEW_PAGE_PATH_SOURCE_LANDING } from '@/router/routes/constants';
import { trackingClickEvent } from '@/tracking/tracking';
import { TRACKING_ACTION, TRACKING_LABEL } from '@/tracking/types';
import { HgBadge, HgButton, HgSupportedLanguage } from '@smg-real-estate/domus';
import { computed, onMounted, useTemplateRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';

const isHg = isPlatformHg();
const { locale, t } = useI18n();
const router = useRouter();
const phoneMockupImagesHg: Record<HgSupportedLanguage, string> = {
  [HgSupportedLanguage.DE]: getPropertyLandingImageUrl('market-trends/phone_mockup_hg_de.png'),
  [HgSupportedLanguage.EN]: getPropertyLandingImageUrl('market-trends/phone_mockup_hg_en.png'),
  [HgSupportedLanguage.FR]: getPropertyLandingImageUrl('market-trends/phone_mockup_hg_fr.png'),
  [HgSupportedLanguage.IT]: getPropertyLandingImageUrl('market-trends/phone_mockup_hg_it.png'),
};

const phoneMockupImagesIs24: Record<HgSupportedLanguage, string> = {
  [HgSupportedLanguage.DE]: getPropertyLandingImageUrl('market-trends/phone_mockup_is24_de.png'),
  [HgSupportedLanguage.EN]: getPropertyLandingImageUrl('market-trends/phone_mockup_is24_en.png'),
  [HgSupportedLanguage.FR]: getPropertyLandingImageUrl('market-trends/phone_mockup_is24_fr.png'),
  [HgSupportedLanguage.IT]: getPropertyLandingImageUrl('market-trends/phone_mockup_is24_it.png'),
};

const phoneMockupImage = computed(() => {
  const imagesByLocale = isHg ? phoneMockupImagesHg : phoneMockupImagesIs24;
  const currentLocale = locale.value as HgSupportedLanguage;

  return imagesByLocale[currentLocale] ?? imagesByLocale[HgSupportedLanguage.EN];
});

const goToOverview = () => {
  trackingClickEvent(
    TRACKING_ACTION.GO_TO_MY_REAL_ESTATE,
    TRACKING_LABEL.PROPERTY_PRICE_TREND_TEASER,
  );

  router.push(OVERVIEW_PAGE_PATH_SOURCE_LANDING[locale.value]);
};

const componentRef = useTemplateRef('price-evolution');
const route = useRoute();
onMounted(() => {
  if (route.hash === '#price-evolution') {
    componentRef.value?.scrollIntoView({ behavior: 'smooth' });
  }
});
</script>

<style lang="scss" module>
@use '@/assets/variables' as *;

.section {
  margin-top: $space-300;
  margin-bottom: $space-600;
}

.card {
  background-color: $color-blue-0;
  border-radius: $border-radius-xl;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: $space-100 $space-100 0;
  gap: $space-200;

  &.is24 {
    background-color: $color-teal-90;
  }

  @include grid-media-query(sm) {
    flex-direction: row;
    align-items: flex-stretch;
    padding: $space-200 $space-200 0 $space-200;
    gap: $space-400;
  }

  @include grid-media-query(md) {
    padding: $space-300 $space-300 0 $space-300;
    gap: $space-200;
  }
}

.content {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  padding-bottom: $space-200;
}

.badge {
  background-color: #ffeef6;
  color: #d61f69;
}

.title {
  @include heading-1;
  color: $color-black;
  max-width: 500px;
  text-wrap: balance;
  hyphens: none;
  -webkit-hyphens: none;
  word-break: normal;
  overflow-wrap: normal;
}

.description {
  @include body-2;
  margin: 0 0 $space-200 0;
  color: $color-black;
  max-width: 500px;
}

.cta {
  width: 100%;
  color: $color-blue-60;

  @include grid-media-query(sm) {
    width: auto;
    min-width: 240px;
  }
}

.imageWrapper {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;
}

.image {
  width: 100%;
  max-width: 400px;
  height: auto;
  display: block;
  filter: drop-shadow(0 $space-25 $space-50 $elevation-shadow-color);
  margin-top: auto; // Extra insurance to push it down
}
</style>
